import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

// 15-minute steps across the day: value "HH:MM", label "h:mm AM"
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4), m = (i % 4) * 15;
  const hh = String(h).padStart(2, "0"), mm = String(m).padStart(2, "0");
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { value: `${hh}:${mm}`, label: `${h12}:${mm} ${ampm}` };
});

export function tzAbbrev(tz, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch { return ""; }
}

// Convert a UTC ISO string to a workspace-local "YYYY-MM-DDTHH:MM" for prefilling the picker.
export function utcToLocalInput(iso, tz) {
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((p) => p.type === t)?.value;
    let hour = g("hour"); if (hour === "24") hour = "00";
    return `${g("year")}-${g("month")}-${g("day")}T${hour}:${g("minute")}`;
  } catch { return ""; }
}

// value: "YYYY-MM-DDTHH:MM" (workspace-local wall time) | onChange(str) | tz: IANA name
export function DateTimePicker({ value, onChange, tz = "UTC", testId, placeholder = "Pick date & time", className }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(value) : null;
  const time = value ? value.slice(11, 16) : "09:00";

  const commit = (dateObj, timeStr) => {
    const d = format(dateObj, "yyyy-MM-dd");
    onChange(`${d}T${timeStr}`);
  };
  const onDate = (d) => { if (d) commit(d, time); };
  const onTime = (t) => { commit(parsed || new Date(), t); };

  const label = parsed
    ? `${format(parsed, "EEE MMM d")}, ${format(parsed, "h:mm a")} ${tzAbbrev(tz, parsed)}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testId}
          className={cn("justify-start text-left font-normal", !parsed && "text-muted-foreground", className)}>
          <CalendarDays className="h-4 w-4 mr-2 shrink-0" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={parsed || undefined} onSelect={onDate} initialFocus />
        <div className="p-3 border-t border-border/60">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
            Time ({tzAbbrev(tz, parsed || new Date())})
          </div>
          <Select value={time} onValueChange={onTime}>
            <SelectTrigger data-testid={testId ? `${testId}-time` : undefined}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {TIME_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="w-full mt-3 rounded-md" onClick={() => setOpen(false)} data-testid={testId ? `${testId}-done` : undefined}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
